import { useEffect, useMemo } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { loadOrders, } from '../redux/actions';
/**
 * The `useOrders` hook returns an object representing the current user's
 * orders.
 *
 * This hook has a side effect that will load all the user's orders upon
 * calling it, if not already loaded. Optionally, an object can be passed to
 * the hook to expand the `orders` object to include additional properties.
 * This looks like `useOrders({ includeDetails: true })`. This `includeDetails`
 * option only applies to the initial load of the orders.
 *
 * @example
 *
 * ```javascript
 * import { useOrders } from '@chordcommerce/react-autonomy'
 *
 * const OrdersPage = () => {
 *   const { orders, isLoaded, isFetching, error } = useOrders()
 *
 *   if (isFetching) {
 *     return <p>Loading…</p>
 *   }
 *
 *   return (
 *     <dl>
 *       {orders.map(order => (
 *         <React.Fragment key={order.number}>
 *           <dt>Order #{order.number}</dt>
 *           <dd>
 *             Total price: {order.displayTotal}
 *             <br />
 *             (Items: {order.lineItems.map(li => li.sku).join(', ')})
 *           </dd>
 *         </React.Fragment>
 *       )}
 *     </dl>
 *   )
 * }
 * ```
 *
 * @category Hook
 */
export function useOrders(options) {
    const ordersState = useSelector((state) => state.orders, shallowEqual);
    const isFetching = ordersState.loading.includes('list');
    const isLoaded = ordersState.success.includes('list');
    const error = ordersState.error?.list;
    const dispatch = useDispatch();
    const actionDispatches = useMemo(() => bindActionCreators({
        loadOrders,
    }, dispatch), [dispatch]);
    useEffect(() => {
        if (isFetching || isLoaded || error)
            return;
        if (Object.keys(ordersState.data).length === 0) {
            actionDispatches.loadOrders(options);
        }
    }, [ordersState]);
    const flatOrders = useMemo(() => Object.values(ordersState.data), [ordersState.data]);
    return {
        orders: flatOrders,
        isLoaded,
        isFetching,
        error,
        loadOrders: actionDispatches.loadOrders,
    };
}
